import { DateTime } from "luxon";
import React from "react";
import { Updater, useImmer } from "use-immer";

import {
  IgButton,
  IgCheckbox,
  IgRadio,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { PauseCancelReasonDto } from "@intelligems/ig-types";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useAdjustBilling } from "@src/api/stripe/adjust-billing";
import { FormCheckboxes } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/FormCheckboxes";
import { CloseIcon } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/Forms/CloseIcon";
import { PauseItems } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/Forms/PauseForm/pause-items";
import { getPauseCancelReasons } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/Forms/utils";
import { ContentProps } from "@src/components/BillingPanel/components/ManageSubscriptionsDialog/types";
import { IgAlertSimpleInverted } from "@src/components/Utils/IgAlertSimpleInverted";

export const PauseForm = ({
  handleClose,
  stripeSubscriptionLinkId,
}: ContentProps) => {
  const [isOther, setIsOther] = useImmer<boolean>(false);
  const [otherFeedback, setOtherFeedback] = useImmer<string>("");
  const [willReturn, setWillReturn] = useImmer<boolean>(false);
  const [returnDate, setReturnDate] = useImmer<DateTime | null>(null);
  const [items, setItems] = useImmer(PauseItems);

  const adjustBilling = useAdjustBilling();

  const onItemClick = (
    setItems: Updater<PauseCancelReasonDto[]>,
    value: string
  ) => {
    setItems((draft) => {
      const itemIndex = draft.findIndex((item) => item.value === value);

      for (let i = 0; i < draft.length; i++) {
        draft[i].checked = i === itemIndex;
      }
      return draft;
    });
  };

  const handlePauseClick = () => {
    adjustBilling.mutate(
      {
        pathParams: undefined,
        queryParams: undefined,
        body: {
          stripeSubscriptionLinkId,
          action: "pause",
          reasons: getPauseCancelReasons(items),
        },
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  return (
    <>
      <DialogTitle sx={{ padding: "0 0 20px 0" }}>
        <Grid container direction={"column"} rowSpacing={2}>
          <Grid item>
            <IgTypography variant={"h2"}>We'll Pause Your Plan</IgTypography>
          </Grid>
          <CloseIcon handleClose={handleClose} />
        </Grid>
        <Grid item mt={1}>
          <IgAlertSimpleInverted
            alertKey={"pause-plan"}
            severity={"warning"}
            colorBackground={true}
            message={
              <IgTypography variant={"body2"}>
                You will be charged{" "}
                <IgTypography variant={"body2"} sx={{ fontWeight: 600 }}>
                  $9 per month
                </IgTypography>{" "}
                while your plan is paused.
              </IgTypography>
            }
          />
        </Grid>
      </DialogTitle>
      <DialogContent sx={{ padding: "0 0 20px 0" }}>
        <Grid container item direction={"column"} rowSpacing={2}>
          <Grid item>
            <IgTypography>
              What's your reason for pausing your plan?
            </IgTypography>
          </Grid>
          <Grid item>
            <FormCheckboxes
              disabled={adjustBilling.isPending}
              items={items}
              onItemClick={(value, checked) => onItemClick(setItems, value)}
              onSubItemClick={(
                itemValue: string,
                subItemValue: string,
                checked: boolean
              ) => {}}
            />
            <Grid item>
              <FormControlLabel
                control={
                  <IgCheckbox
                    disabled={adjustBilling.isPending}
                    size={"small"}
                    onChange={(event) => {
                      setIsOther(event.target.checked);
                      if (!event.target.checked) {
                        setOtherFeedback("");
                      }
                    }}
                    checked={isOther}
                  />
                }
                label={"Other"}
              />
            </Grid>
          </Grid>
          {isOther ? (
            <Grid item pr={1}>
              <IgTextField
                disabled={adjustBilling.isPending}
                onChange={(e) => setOtherFeedback(e.target.value)}
                value={otherFeedback}
                multiline
                fullWidth
                placeholder={"Share your feedback here"}
              />
            </Grid>
          ) : null}
          <Grid item>
            <IgTypography>Do you have a planned return date?</IgTypography>
          </Grid>
          <Grid container item columnSpacing={2}>
            <Grid item>
              <FormControlLabel
                control={
                  <IgRadio
                    disabled={adjustBilling.isPending}
                    checked={willReturn}
                    onChange={() => {
                      setWillReturn(true);
                    }}
                  />
                }
                label={"Yes"}
              />
            </Grid>
            {willReturn ? (
              <Grid item>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                  <DatePicker
                    disabled={adjustBilling.isPending}
                    value={returnDate}
                    onChange={(newValue) => {
                      setReturnDate(newValue);
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            ) : null}
            <Grid item>
              <FormControlLabel
                control={
                  <IgRadio
                    disabled={adjustBilling.isPending}
                    checked={!willReturn}
                    onChange={() => {
                      setWillReturn(false);
                      setReturnDate(null);
                    }}
                  />
                }
                label={"No"}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <IgButton
          variant={"contained"}
          onClick={handlePauseClick}
          loading={adjustBilling.isPending}
        >
          Confirm Plan Pause
        </IgButton>
      </DialogActions>
    </>
  );
};
